<template>
  <div class="bg-gray-50 min-h-screen py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h3 class="text-2xl font-semibold text-gray-900 mb-4">
        School/Academy-level Module Questionnaire Report
      </h3>

      <div class="bg-white shadow-md rounded-lg p-6 mb-8">
        <label for="survey-select" class="block text-sm font-medium text-gray-700 mb-2">Select Survey</label>
        <div class="relative">
          <select id="survey-select" v-model="selectedSurveyId" @change="loadSurveyData"
            class="border block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option value="">Select a survey</option>
            <option v-for="survey in surveys" :key="survey.id" :value="survey.id">
              {{ survey.title }}
            </option>
          </select>

        </div>
        <div v-if="survey && survey.surveyMode === 'recurring'" class="relative mt-2 mb-4">
          View By Date:
          <select id="date-select" v-model="selectedDate" @change="loadSurveyData"
            class="border block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option value="">ALL</option>
            <option v-for="date in generateDates(survey)" :key="date" :value="date">
              {{ new Date(date).toLocaleDateString() }}
            </option>
          </select>

        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>

      <div v-else-if="error" class="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">
              {{ error }}
            </p>
          </div>
        </div>
      </div>

      <div v-else-if="selectedSurveyId && !loading" class="space-y-8">
        <div v-for="question in surveyData.questions" :key="question.id"
          class="bg-white shadow-md rounded-lg overflow-hidden">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-xl font-semibold text-gray-900">
                {{ question.text }}
              </h2>
              <div class="flex items-center" v-if="question.type != 'text'">
                <span class="mr-2 text-sm text-gray-600">Line</span>
                <button @click="toggleChartType(question.id)" type="button" :class="[
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
                  chartTypes[question.id] === 'pie'
                    ? 'bg-indigo-600'
                    : 'bg-gray-200',
                ]">
                  <span :class="[
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                    chartTypes[question.id] === 'pie'
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  ]" aria-hidden="true"></span>
                </button>
                <span class="ml-2 text-sm text-gray-600">Pie</span>
              </div>
            </div>
            <div class="font-semibold text-gray-900 flex justify-between items-center font-semibold text-gray-900"
              style="font-size: 16px;">
              <span>
                Question Type: {{ getQuestionOption(question) }}.
              </span>
            </div>

            <div class="h-80" v-if="question.type != 'text'">
              <PieChart v-if="chartTypes[question.id] === 'pie'" :chart-data="getPieChartData(question)" />
              <LineChart v-else :chart-data="getLineChartData(question)" />
            </div>
            <div v-else>
              <!-- table for list comments -->
              <div class="overflow-x-auto mt-2">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-black text-white">
                    <tr>
                      <th scope="col"
                        class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Module Code
                      </th>
                      <th scope="col"
                        class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Staff
                      </th>
                      <th scope="col"
                        class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Comments
                      </th>
                     
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="response in question.options" :key="response.id">
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div class="text-sm text-gray-900">{{ response.text }}</div>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div class="text-sm text-gray-900">{{ getStaffName(response.id) }}</div>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div class="text-sm text-gray-900">{{ response.answers }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="bg-white shadow-md rounded-lg p-6 text-center">
        <p class="text-gray-600">Please select a survey to view the report.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PieChart from "../charts/PieChart.vue";
import LineChart from "../charts/LineChart.vue";
import { surveyApi } from "@/api/surveyManagement";

export default {
  name: "SchoolDepartmentSurveyReport",
  components: {
    PieChart,
    LineChart,
  },
  data() {
    return {
      selectedSurveyId: "",
      survey: null,
      surveyData: null,
      loading: false,
      error: null,
      chartTypes: {},
      surveys: [],
      selectedDate: ""
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      departments: (state) => state.departments,
      schools: (state) => state.schools,
      staff: (state) => state.staff,
    }),
  },
  methods: {
    getStaffName(id){
      let _staff = this.staff.find(x => x.staffId == id)
      return _staff ? _staff.name : ""
    },
    getQuestionOption(question) {
      let _survey = this.surveys.find(x => x.id == this.selectedSurveyId)
      if (_survey)
        return _survey.questions.find(x => x.text == question.text).option == "module/staff" ? " Module/Staff" : " Module";
      return "";
    },
    async fetchSurveys() {
      try {
        this.loading = true;
        let _surveys = await surveyApi.getDeptAll();
        console.log(_surveys)

        _surveys = _surveys.filter((survey) => survey.type != "University" && survey.status == 'Published');
        if (this.user.groups.length > 0) {
          let _school = this.schools.find(x => x.fullName == this.user.groups[0].identity).shortName
          let _departments = this.departments.filter(x => x.school == _school).map(o => o.fullName)
          console.log(_departments)
          _surveys = _surveys.filter(x => x.department == this.user.groups[0].identity || _departments.find(d => d == x.department))
        }
        else if (this.user.manages)
          _surveys = _surveys.filter(x => x.department == this.user.manages)

        this.surveys = _surveys
      } catch (error) {
        console.error("Error fetching surveys:", error);
        this.error = "Failed to load surveys. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    generateDates(survey) {
      if (survey.surveyMode === 'recurring') {
        console.log(survey.recurringPattern)
        //genereta dates from start date to end date with daily or weekly
        let _dates = []
        let _startDate = new Date(survey.startDate)
        let _endDate = new Date(survey.endDate)
        //if _endDate is after today, set it to today
        if (_endDate > new Date())
          _endDate = new Date()
        let _date = new Date(_startDate)
        while (_date <= _endDate) {
          _dates.push(new Date(_date))
          if (survey.recurringPattern == "weekly")
            _date.setDate(_date.getDate() + 7)
          else
            _date.setDate(_date.getDate() + 1)
        }
        return _dates
      }
      return []
    },
    async loadSurveyData() {
      if (!this.selectedSurveyId) {
        this.surveyData = null;
        return;
      }
      this.survey = this.surveys.find(x => x.id == this.selectedSurveyId)
      this.loading = true;
      this.error = null;

      try {
        let date = this.selectedDate ? new Date(new Date(this.selectedDate).getTime() + 8 * 60 * 60 * 1000).toISOString().split('T')[0] : ""
        const response = await surveyApi.getSurveyResults(
          this.selectedSurveyId, date
        );

        this.surveyData = response;
        // Initialize chart types
        this.chartTypes = {};
        this.surveyData.questions.forEach((question) => {
          this.$set(
            this.chartTypes,
            question.id,
            "pie"
          );
        });
      } catch (error) {
        console.error("Error fetching survey data:", error);
        this.error = "Failed to load survey data. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    getPieChartData(question) {
      const labels = question.options.map((option) => option.text);
      const data = question.options.map((option) => option.count);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: [
              "#3B82F6", // blue-500
              "#EF4444", // red-500
              "#10B981", // green-500
              "#F59E0B", // yellow-500
              "#6366F1", // indigo-500
              "#EC4899", // pink-500
            ],
          },
        ],
      };
    },
    getLineChartData(question) {
      const labels = question.options.map((option) => option.text);
      const data = question.options.map((option) => option.count);

      return {
        labels,
        datasets: [
          {
            label: "Responses",
            data,
            fill: false,
            borderColor: "#3B82F6", // blue-500
            tension: 0.1,
            pointBackgroundColor: "#3B82F6",
            pointBorderColor: "#3B82F6",
            pointHoverBackgroundColor: "#2563EB", // blue-600
            pointHoverBorderColor: "#2563EB",
          },
        ],
      };
    },
    toggleChartType(questionId) {
      this.$set(
        this.chartTypes,
        questionId,
        this.chartTypes[questionId] === "pie" ? "line" : "pie"
      );
    },
  },
  created() {
    this.fetchSurveys();
  },
};
</script>